import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  ArrowRight, 
  ArrowDown, 
  CheckCircle2, 
  Clock, 
  DollarSign, 
  Package, 
  Send, 
  FileText,
  Truck,
  Building2,
  Users,
  Zap,
  Shield,
  ChevronRight,
  Play,
  Pause,
  RotateCcw,
  Sparkles,
  Droplets
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BackToHome from "@/components/BackToHome";
import SiteHeader from "@/components/SiteHeader";

// Image URLs
const IMAGES = {
  step1: "https://static.prod-images.emergentagent.com/jobs/def91b66-b659-4903-b2e6-99938248bd60/images/f3ccf8bec5a7151cee5fbb796a274f9294cdca714223f9b45b7ea60877571a77.png",
  step2: "https://static.prod-images.emergentagent.com/jobs/def91b66-b659-4903-b2e6-99938248bd60/images/cf7a91cc494a276f14bdab3c889982e74d35aa9963348a9edeafe3d63812f1eb.png",
  step3: "https://static.prod-images.emergentagent.com/jobs/def91b66-b659-4903-b2e6-99938248bd60/images/ebc9c9355ce31e7e3d2370c58f6fefba48ebe5e24dee64ccf8b6c40516192adf.png",
  step4: "https://static.prod-images.emergentagent.com/jobs/def91b66-b659-4903-b2e6-99938248bd60/images/08604b90350f7afcfdcb3d2633ed95493ef4486c70892637417bb51cdd33e9f3.png",
  step5: "https://static.prod-images.emergentagent.com/jobs/def91b66-b659-4903-b2e6-99938248bd60/images/735090133a9a6ce0eaff5f4d983b2cc00f2e84ec69580ab1d0869d03b94b2669.png",
  step6: "https://static.prod-images.emergentagent.com/jobs/def91b66-b659-4903-b2e6-99938248bd60/images/3e67d09b2c30df5486a72f4f15c07dd07d5ffb9d5479feda90218f1c315a078e.png"
};

const STEPS = [
  {
    id: 1,
    title: "Submit Your Request",
    subtitle: "You Need It, We Hunt It",
    description: "Your supply chain team submits a backorder request through AskIsaac. Simply enter the item details, quantity needed, and any urgency level. It takes less than 2 minutes.",
    details: [
      "Enter item name and manufacturer number",
      "Specify quantity and urgency level",
      "Add any special notes or requirements",
      "Submit with one click"
    ],
    image: IMAGES.step1,
    icon: FileText,
    color: "blue"
  },
  {
    id: 2,
    title: "AskIsaac Takes Over",
    subtitle: "",
    description: "The moment your request hits our system, AskIsaac springs into action. Our intelligent platform processes your request and prepares to broadcast it to our nationwide supplier network.",
    details: [
      "Request verified and processed instantly",
      "Item details formatted for suppliers",
      "Blind bid request prepared",
      "Ready to broadcast in seconds"
    ],
    image: IMAGES.step2,
    icon: Zap,
    color: "purple"
  },
  {
    id: 3,
    title: "Nationwide Broadcast",
    subtitle: "Reaching Suppliers Coast to Coast",
    description: "AskIsaac sends your request to multiple verified suppliers across the country simultaneously. This blind-bid system ensures competitive pricing and maximum availability.",
    details: [
      "Request sent to 50+ verified suppliers",
      "Suppliers don't see each other's bids",
      "Geographic coverage ensures availability",
      "Real-time tracking of responses"
    ],
    image: IMAGES.step3,
    icon: Send,
    color: "cyan"
  },
  {
    id: 4,
    title: "Suppliers Respond",
    subtitle: "Competitive Bids Roll In",
    description: "Suppliers review your request and submit their best offers. Each bid includes price per unit, available quantity, and estimated delivery time. We typically receive multiple bids within 24-48 hours.",
    details: [
      "Suppliers submit competitive bids",
      "Price, quantity, and ETA included",
      "Multiple options to choose from",
      "Best deals surface automatically"
    ],
    image: IMAGES.step4,
    icon: Package,
    color: "amber"
  },
  {
    id: 5,
    title: "You Get the Best Bid",
    subtitle: "Transparent Pricing, Your Choice",
    description: "We present you with the best available option including product details, price, quantity available, and delivery ETA. If you like it, simply provide a PO number – you can even upload it later, we know it takes time!",
    details: [
      "Best price automatically highlighted",
      "Full product details provided",
      "Quantity and ETA clearly shown",
      "Send PO# when ready (upload later OK)"
    ],
    image: IMAGES.step5,
    icon: DollarSign,
    color: "green"
  },
  {
    id: 6,
    title: "Order Secured & Delivered",
    subtitle: "Fast Processing, Reliable Delivery",
    description: "Once you approve, we secure the product immediately. We invoice you like we always do – no surprises. Your order ships directly to your facility with full tracking.",
    details: [
      "Product secured immediately",
      "Standard invoicing process",
      "Full shipment tracking",
      "Delivered to your facility"
    ],
    image: IMAGES.step6,
    icon: Truck,
    color: "emerald"
  }
];

// Animated Step Card
const StepCard = ({ step, isActive, onClick }) => {
  const Icon = step.icon;
  // Dark-theme palette: tinted glass icon bg + bright accent text on dark cards
  const colorClasses = {
    blue:    { bg: "bg-blue-500/15 ring-blue-400/30",       text: "text-blue-300",    activeBg: "bg-blue-500" },
    purple:  { bg: "bg-purple-500/15 ring-purple-400/30",   text: "text-purple-300",  activeBg: "bg-purple-500" },
    cyan:    { bg: "bg-cyan-500/15 ring-cyan-400/30",       text: "text-cyan-700",    activeBg: "bg-cyan-500" },
    amber:   { bg: "bg-amber-500/15 ring-amber-400/30",     text: "text-amber-700",   activeBg: "bg-amber-500" },
    green:   { bg: "bg-emerald-500/15 ring-emerald-400/30", text: "text-emerald-300", activeBg: "bg-emerald-500" },
    emerald: { bg: "bg-emerald-500/15 ring-emerald-400/30", text: "text-emerald-300", activeBg: "bg-emerald-500" },
  };
  const colors = colorClasses[step.color] || colorClasses.cyan;

  return (
    <div 
      onClick={onClick}
      className={`
        cursor-pointer transition-all duration-500 rounded-2xl overflow-hidden ring-1
        ${isActive 
          ? `ring-2 ring-amber-400 shadow-2xl shadow-amber-500/20 scale-[1.02] bg-white` 
          : 'ring-slate-200 bg-white hover:bg-slate-50 hover:ring-slate-300 hover:scale-[1.01]'
        }
      `}
    >
      {/* Image */}
      <div className="relative aspect-video overflow-hidden">
        <img 
          src={step.image} 
          alt={step.title}
          className={`w-full h-full object-cover transition-transform duration-700 ${isActive ? 'scale-105' : ''}`}
        />
        <div className={`absolute inset-0 bg-gradient-to-t from-[#0c1939]/80 via-[#0c1939]/20 to-transparent`} />
        
        {/* Step Number Badge */}
        <div className={`absolute top-4 left-4 w-12 h-12 ${isActive ? `${colors.activeBg} text-slate-900` : 'bg-white/95 text-slate-900'} rounded-xl flex items-center justify-center shadow-lg font-bold`}>
          <span className="text-xl">{step.id}</span>
        </div>

        {/* Active Indicator */}
        {isActive && (
          <div className="absolute top-4 right-4 flex items-center gap-2 bg-emerald-500/90 backdrop-blur-sm px-3 py-1.5 rounded-full">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
            <span className="text-xs font-bold text-slate-900">Now Playing</span>
          </div>
        )}

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className={`text-xs font-medium ${isActive ? 'text-amber-800' : 'text-slate-600'} mb-1`}>{step.subtitle}</p>
          <h3 className="text-xl font-bold text-slate-900">{step.title}</h3>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <p className="text-slate-600 text-sm mb-4">{step.description}</p>
        
        <div className={`space-y-2 ${isActive ? 'opacity-100' : 'opacity-80'}`}>
          {step.details.map((detail, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <CheckCircle2 className={`w-4 h-4 ${colors.text} flex-shrink-0 mt-0.5`} />
              <span className="text-sm text-slate-700">{detail}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Flow Arrow
const FlowArrow = ({ vertical = false }) => (
  <div className={`flex items-center justify-center ${vertical ? 'py-2' : 'px-2'}`}>
    {vertical ? (
      <ArrowDown className="w-8 h-8 text-blue-300 animate-bounce" />
    ) : (
      <ArrowRight className="w-8 h-8 text-blue-300" />
    )}
  </div>
);

export default function HowItWorksPage() {
  const [activeStep, setActiveStep] = useState(1);
  const [isPlaying, setIsPlaying] = useState(true);
  const navigate = useNavigate();

  // Same anchor-scroll helper used on BackorderSourcingPage so both CTAs land
  // the user on the homepage Quick Quote / Backorder Request form (`#quick-quote`).
  // Plain `<Link to="/#quick-quote">` doesn't reliably scroll because React
  // Router doesn't natively follow hash anchors after a route change, so we
  // poll for the target element after navigation.
  const goToQuickQuote = () => {
    navigate("/");
    let attempts = 0;
    const tick = () => {
      attempts += 1;
      const el = document.getElementById("quick-quote");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempts < 30) setTimeout(tick, 100);
    };
    setTimeout(tick, 100);
  };

  // Auto-advance through steps
  useEffect(() => {
    if (!isPlaying) return;
    
    const timer = setInterval(() => {
      setActiveStep(prev => prev >= 6 ? 1 : prev + 1);
    }, 5000);

    return () => clearInterval(timer);
  }, [isPlaying]);

  const handleStepClick = (stepId) => {
    setActiveStep(stepId);
    setIsPlaying(false);
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleRestart = () => {
    setActiveStep(1);
    setIsPlaying(true);
  };

  const currentStep = STEPS.find(s => s.id === activeStep);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="how-it-works-page">
      <SiteHeader />
      {/* Hero Section — white, two-column, supply-chain partner image on the right */}
      <div className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800" />
        <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_top_right,rgba(14,165,233,0.18),transparent_55%)]" />
        <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(245,158,11,0.10),transparent_55%)]" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="flex items-center gap-2 mb-4 text-sm">
            <Link to="/" className="text-cyan-300 hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4 text-cyan-500/60" />
            <span className="text-amber-400 font-semibold">How It Works</span>
          </div>

          <div className="grid lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] gap-10 lg:gap-14 items-center">
            {/* LEFT: text */}
            <div>
              <div className="inline-flex items-center gap-2 bg-cyan-500/10 ring-1 ring-cyan-400/40 px-3 py-1.5 rounded-full mb-5 backdrop-blur">
                <Play className="w-3.5 h-3.5 text-cyan-300" />
                <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-cyan-200">
                  Medical Supply Chain &amp; Infrastructure Partner to Hospitals
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 font-manrope tracking-tight leading-[1.05] text-white">
                Expand Your Healthcare Network <span className="text-cyan-300">On Demand</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8">
                Enhance your GPO relationships with an extended network of verified suppliers and contractors. Our QuickBid Exchange
                delivers competitive bids in <span className="text-amber-300 font-semibold">hours — not weeks</span>. More options, better pricing, full compliance.
              </p>

              {/* Key Benefits */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-800/60 rounded-xl p-4 ring-1 ring-slate-700 backdrop-blur shadow-sm">
                  <Clock className="w-6 h-6 text-cyan-300 mb-2" />
                  <p className="text-2xl font-bold text-white">Hours</p>
                  <p className="text-slate-400 text-sm">Not Weeks</p>
                </div>
                <div className="bg-slate-800/60 rounded-xl p-4 ring-1 ring-slate-700 backdrop-blur shadow-sm">
                  <Users className="w-6 h-6 text-cyan-300 mb-2" />
                  <p className="text-2xl font-bold text-white">50+</p>
                  <p className="text-slate-400 text-sm">Pre-Vetted Partners</p>
                </div>
                <div className="bg-slate-800/60 rounded-xl p-4 ring-1 ring-slate-700 backdrop-blur shadow-sm">
                  <Shield className="w-6 h-6 text-amber-400 mb-2" />
                  <p className="text-2xl font-bold text-white">$500K+</p>
                  <p className="text-slate-400 text-sm">Annual Savings</p>
                </div>
              </div>
            </div>

            {/* RIGHT: diverse healthcare team hero photo */}
            <div className="relative aspect-[4/5] lg:aspect-auto lg:h-[520px] rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(2,6,23,0.7)] ring-1 ring-slate-700" data-testid="howitworks-hero-image">
              <img
                src="/api/static/email_assets/aug26_diverse_healthcare_team.jpg"
                alt="Diverse team of medical professionals in scrubs reviewing supply inventory — Sterile Mate hospital supply chain partner"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Left-edge fade bleeds the image cleanly into the dark hero background */}
              <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-slate-950 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-slate-950/85 via-slate-950/40 to-transparent" />
              <div className="absolute bottom-4 left-6 right-6 text-white">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300 mb-1">Supply Chain &middot; Facilities &middot; Construction</p>
                <p className="text-sm md:text-base font-semibold leading-tight">One partner. One dispatch center. Every case, every corridor.</p>
              </div>
            </div>
          </div>
        </div>
        <div className="relative z-10 h-1 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Hospital backorder sourcing dedicated callout */}
        <Link
          to="/how-it-works/backorder-sourcing"
          data-testid="howitworks-backorder-callout"
          className="group block mb-8 rounded-2xl ring-1 ring-amber-300 bg-gradient-to-br from-amber-50 via-white to-cyan-50 hover:ring-amber-400 hover:shadow-2xl hover:shadow-amber-500/10 transition-all overflow-hidden"
        >
          <div className="grid md:grid-cols-[auto_1fr_auto] items-center gap-4 p-5 md:p-6">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30 flex-shrink-0">
              <Building2 className="w-6 h-6 md:w-7 md:h-7 text-slate-900" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-extrabold uppercase tracking-wider bg-amber-500 text-slate-900 px-2 py-0.5 rounded-full">
                  For Hospitals
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-800">
                  Procurement &amp; Supply Chain
                </span>
              </div>
              <h3 className="text-lg md:text-xl font-extrabold text-slate-900 leading-tight mb-1">
                Backorder Sourcing on Behalf of Hospitals
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                See the dedicated 5-step workflow — how Sterile Mate hunts allocated SKUs across our nationwide supplier network and delivers a single transparent estimate to your dock.
              </p>
            </div>
            <div className="flex items-center gap-1.5 text-amber-700 font-bold text-sm whitespace-nowrap group-hover:translate-x-1 transition-transform">
              Read the walkthrough
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </Link>

        {/* Progress Bar & Controls */}
        <div className="bg-white ring-1 ring-slate-200 rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-900 font-manrope">
              Step {activeStep} of 6: <span className="text-cyan-700">{currentStep?.title}</span>
            </h2>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRestart}
                className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Restart
              </Button>
              <Button 
                size="sm" 
                onClick={handlePlayPause}
                className={isPlaying ? "bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold" : "bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold"}
              >
                {isPlaying ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                {isPlaying ? "Pause" : "Play"}
              </Button>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2">
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isComplete = step.id < activeStep;
              const isCurrent = step.id === activeStep;
              
              return (
                <div key={step.id} className="flex items-center flex-1">
                  <button
                    onClick={() => handleStepClick(step.id)}
                    className={`
                      flex items-center gap-2 px-3 py-2 rounded-lg transition-all w-full
                      ${isCurrent 
                        ? 'bg-amber-500 text-slate-900 font-bold shadow-lg shadow-amber-500/30' 
                        : isComplete 
                          ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500/40 font-semibold' 
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200 ring-1 ring-slate-200'
                      }
                    `}
                  >
                    {isComplete ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                    <span className="text-xs font-medium hidden md:inline truncate">{step.title}</span>
                    <span className="text-xs font-medium md:hidden">{step.id}</span>
                  </button>
                  {idx < STEPS.length - 1 && (
                    <ArrowRight className={`w-4 h-4 mx-1 flex-shrink-0 ${step.id < activeStep ? 'text-emerald-500' : 'text-slate-400'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Featured Step - Large Display */}
        <div className="bg-white ring-1 ring-slate-200 rounded-2xl shadow-lg overflow-hidden mb-12">
          <div className="grid lg:grid-cols-2">
            {/* Image Side */}
            <div className="relative aspect-video lg:aspect-auto">
              <img 
                src={currentStep?.image} 
                alt={currentStep?.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0c1939]/60 via-transparent to-transparent" />
            </div>

            {/* Content Side */}
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-14 h-14 bg-${currentStep?.color}-50 ring-1 ring-${currentStep?.color}-500/30 rounded-xl flex items-center justify-center`}>
                  {currentStep && <currentStep.icon className={`w-7 h-7 text-${currentStep?.color}-700`} />}
                </div>
                <div>
                  <p className="text-sm text-slate-500">{currentStep?.subtitle}</p>
                  <h3 className="text-2xl font-bold text-slate-900 font-manrope">{currentStep?.title}</h3>
                </div>
              </div>

              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                {currentStep?.description}
              </p>

              <div className="space-y-3 mb-8">
                {currentStep?.details.map((detail, idx) => (
                  <div key={idx} className="flex items-start gap-3 animate-fade-in" style={{ animationDelay: `${idx * 100}ms` }}>
                    <div className="w-6 h-6 bg-emerald-50 ring-1 ring-emerald-500/40 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-slate-700">{detail}</span>
                  </div>
                ))}
              </div>

              {activeStep === 6 && (
                <Button
                  onClick={goToQuickQuote}
                  data-testid="hiw-step6-start-request"
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold w-full lg:w-auto shadow-lg shadow-amber-500/30"
                >
                  Start Your First Request
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* All Steps Grid */}
        <h2 className="text-2xl font-bold text-slate-900 mb-6 font-manrope">All Steps at a Glance</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {STEPS.map((step) => (
            <StepCard 
              key={step.id} 
              step={step} 
              isActive={step.id === activeStep}
              onClick={() => handleStepClick(step.id)}
            />
          ))}
        </div>

        {/* Why It's Better Section — dark corporate B2B container */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 ring-1 ring-slate-700 rounded-2xl p-8 md:p-12 mb-12 shadow-[0_30px_80px_-20px_rgba(2,6,23,0.55)]">
          <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_top_right,rgba(14,165,233,0.14),transparent_55%)]" />
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 bg-cyan-500/10 ring-1 ring-cyan-400/40 px-3 py-1.5 rounded-full mb-5 backdrop-blur">
              <Zap className="w-3.5 h-3.5 text-cyan-300" />
              <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-cyan-200">
                Measurable Advantage for Materials Management
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-3 font-manrope tracking-tight text-white">
              Why This Process Is <span className="text-cyan-300">Better</span>
            </h2>
            <p className="text-slate-300 max-w-3xl mb-8 leading-relaxed">
              Built for supply-chain and procurement teams who already know how the hospital PO cycle works —
              we plug into your process, cut the phone-and-fax rounds, and hand you back time.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/95 ring-1 ring-slate-200 shadow-lg rounded-xl p-6 backdrop-blur">
                <div className="w-12 h-12 rounded-lg bg-cyan-50 ring-1 ring-cyan-500/30 flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-cyan-700" />
                </div>
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-cyan-700 mb-1.5">Speed to Bid</p>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Days &rarr; Hours</h3>
                <p className="text-slate-600 leading-relaxed">
                  Instead of calling suppliers one by one, we broadcast the request to every vetted partner simultaneously.
                  Bids typically return the same business day.
                </p>
              </div>
              <div className="bg-white/95 ring-1 ring-slate-200 shadow-lg rounded-xl p-6 backdrop-blur">
                <div className="w-12 h-12 rounded-lg bg-emerald-50 ring-1 ring-emerald-500/30 flex items-center justify-center mb-4">
                  <DollarSign className="w-6 h-6 text-emerald-700" />
                </div>
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-700 mb-1.5">Competitive Pricing</p>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Blind-Bid Discipline</h3>
                <p className="text-slate-600 leading-relaxed">
                  Suppliers bid without seeing each other&rsquo;s numbers, so pricing lands on the market floor without political
                  handshakes eroding your margin.
                </p>
              </div>
              <div className="bg-white/95 ring-1 ring-slate-200 shadow-lg rounded-xl p-6 backdrop-blur">
                <div className="w-12 h-12 rounded-lg bg-amber-50 ring-1 ring-amber-500/40 flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-amber-700" />
                </div>
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-amber-700 mb-1.5">Process Fit</p>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Zero-Disruption Onboarding</h3>
                <p className="text-slate-600 leading-relaxed">
                  Standard PO / invoice / Net-30 terms. Your AP team keeps its existing workflow &mdash; we adapt to yours,
                  not the other way around.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Explore More Demos Section — same corporate dark treatment */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 ring-1 ring-slate-700 rounded-2xl p-8 md:p-12 mb-12 shadow-[0_30px_80px_-20px_rgba(2,6,23,0.55)]">
          <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(245,158,11,0.10),transparent_55%)]" />
          <div className="relative z-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-amber-500/10 ring-1 ring-amber-400/40 px-3 py-1.5 rounded-full mb-5 backdrop-blur">
                <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-200">
                  Adjacent Verticals
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 font-manrope tracking-tight">Explore More Capabilities</h2>
              <p className="text-slate-300 max-w-2xl mx-auto leading-relaxed">
                Beyond backorder resolution, Sterile Mate offers comprehensive solutions for your facility&rsquo;s needs.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* EVS Demo Card */}
              <Link
                to="/demo/evs"
                className="group bg-white/95 ring-1 ring-slate-200 hover:ring-emerald-500/60 rounded-xl p-6 transition-all hover:shadow-xl hover:shadow-emerald-500/20 backdrop-blur"
                data-testid="how-it-works-evs-link"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-50 ring-1 ring-emerald-500/40 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-emerald-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 mb-1">Janitorial &amp; EVS Solutions</h3>
                    <p className="text-emerald-700 text-sm font-semibold mb-2">Environmental Services Excellence</p>
                    <p className="text-slate-600 text-sm mb-4 leading-relaxed">Hospital-grade cleaning supplies, disinfectants, floor care, and waste management solutions.</p>
                    <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm group-hover:gap-3 transition-all">
                      <Play className="w-4 h-4" />
                      <span>Watch Demo</span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </Link>

              {/* Water Purification Demo Card */}
              <Link
                to="/demo/water-purification"
                className="group bg-white/95 ring-1 ring-slate-200 hover:ring-cyan-500/60 rounded-xl p-6 transition-all hover:shadow-xl hover:shadow-cyan-500/20 backdrop-blur"
                data-testid="how-it-works-water-link"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-50 ring-1 ring-cyan-500/40 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Droplets className="w-6 h-6 text-cyan-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 mb-1">Water Purification Systems</h3>
                    <p className="text-cyan-700 text-sm font-semibold mb-2">AAMI ST108 Compliant Solutions</p>
                    <p className="text-slate-600 text-sm mb-4 leading-relaxed">RO/DI systems, water testing, and compliance solutions. Healthcare water purification experts.</p>
                    <div className="flex items-center gap-2 text-cyan-700 font-semibold text-sm group-hover:gap-3 transition-all">
                      <Play className="w-4 h-4" />
                      <span>Watch Demo</span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            <div className="text-center mt-8">
              <Link
                to="/solutions"
                className="inline-flex items-center gap-2 text-slate-900 bg-amber-500 hover:bg-amber-400 px-6 py-3 rounded-lg font-bold transition-all mr-4 mb-2 shadow-lg shadow-amber-500/30"
                data-testid="view-solutions-link"
              >
                View Complete Solutions Overview
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/demos"
                className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 font-semibold transition-colors"
                data-testid="view-all-demos-link"
              >
                View All Demos
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-amber-50 via-white to-cyan-50 ring-1 ring-amber-200 rounded-2xl shadow-lg p-8 md:p-12 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 font-manrope">Ready to Get Started?</h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Join hundreds of healthcare facilities who&apos;ve streamlined their backorder procurement 
            with AskIsaac. Your first request takes less than 2 minutes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={goToQuickQuote}
              data-testid="hiw-cta-submit-backorder"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-8 py-3 text-lg shadow-lg shadow-amber-500/30"
            >
              Submit a Backorder Request
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Link to="/">
              <Button variant="outline" className="border-cyan-300 bg-white text-cyan-800 hover:bg-cyan-50 hover:text-cyan-900 hover:border-cyan-500 px-8 py-3 text-lg">
                Back to Dashboard
              </Button>
            </Link>
          </div>
          <p className="text-slate-500 text-sm mt-6">
            Questions? Call us at <strong className="text-slate-900">800.988.3892</strong> or email <strong className="text-slate-900">support@sterilemate.com</strong>
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-200 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-500">
            AskIsaac &middot; 800.988.3892 &middot; support@sterilemate.com
          </p>
        </div>
      </footer>
    </div>
  );
}
